import { SafeError, SafeType } from './safeAwait';
import { getRequestDetails } from './httpRequestDetails';

export type OutboundDedupeRequestInit = RequestInit & {
  skipInFlightDedupe?: boolean;
};

const OUTBOUND_IN_FLIGHT_KEY = '__BZ_OUTBOUND_REQUEST_IN_FLIGHT__';

type InFlightMap = Map<string, Promise<SafeType<Response>>>;

const getInFlightMap = (): InFlightMap => {
  const globalScope = globalThis as typeof globalThis & { [OUTBOUND_IN_FLIGHT_KEY]?: InFlightMap };
  if (!globalScope[OUTBOUND_IN_FLIGHT_KEY]) {
    globalScope[OUTBOUND_IN_FLIGHT_KEY] = new Map();
  }
  return globalScope[OUTBOUND_IN_FLIGHT_KEY];
};

const parseDedupeEnabledFromEnv = (): boolean => {
  const flag = process.env.BZ_OUTBOUND_REQUEST_DEDUPE ?? process.env.BZ_UPSTREAM_REQUEST_DEDUPE;
  if (flag === 'false') return false;
  if (flag === 'true') return true;
  return typeof window === 'undefined';
};

export const isOutboundRequestDedupeEnabled = (): boolean => parseDedupeEnabledFromEnv();

const appendHeaders = (target: Headers, source: HeadersInit | undefined): void => {
  if (!source) return;
  if (source instanceof Headers) {
    source.forEach((value, name) => target.append(name, value));
    return;
  }
  if (Array.isArray(source)) {
    for (const [name, value] of source) {
      target.append(name, value);
    }
    return;
  }
  for (const [name, value] of Object.entries(source)) {
    target.append(name, value);
  }
};

const getOutboundRequestHeaders = (input: RequestInfo, init?: RequestInit): Headers => {
  const headers = new Headers();
  const isRequestObject = typeof Request !== 'undefined' && typeof input === 'object' && input instanceof Request;
  if (isRequestObject) {
    input.headers.forEach((value, name) => headers.append(name, value));
  }
  appendHeaders(headers, init?.headers);
  return headers;
};

const serializeHeadersForDedupeKey = (headers: Headers): string => {
  const entries: [string, string][] = [];
  headers.forEach((value, name) => {
    entries.push([name.toLowerCase(), value]);
  });
  entries.sort(([leftName], [rightName]) => leftName.localeCompare(rightName));
  if (entries.length === 0) return '';
  return entries.map(([name, value]) => `${name}=${value}`).join('&');
};

const normalizeUrlForDedupeKey = (url: string): string => {
  try {
    const parsed = new URL(url, 'https://outbound-dedupe.local');
    const sortedParams = [...parsed.searchParams.entries()].sort(([left], [right]) => left.localeCompare(right));
    parsed.search = '';
    for (const [name, value] of sortedParams) {
      parsed.searchParams.append(name, value);
    }
    parsed.hash = '';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return parsed.toString();
    }
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return url;
  }
};

export const buildOutboundRequestDedupeKey = (input: RequestInfo, init?: OutboundDedupeRequestInit): string => {
  const { method, url } = getRequestDetails(input, init);
  const credentials = init?.credentials ?? 'same-origin';
  const headersKey = serializeHeadersForDedupeKey(getOutboundRequestHeaders(input, init));
  const headersPart = headersKey ? `:headers=${headersKey}` : '';
  return `${method}:${normalizeUrlForDedupeKey(url)}:credentials=${credentials}${headersPart}`;
};

export const shouldDedupeOutboundRequest = (input: RequestInfo, init?: OutboundDedupeRequestInit): boolean => {
  if (!isOutboundRequestDedupeEnabled()) return false;
  if (init?.skipInFlightDedupe) return false;
  if (init?.signal) return false;
  const { method, url } = getRequestDetails(input, init);
  const MAX_URL_FOR_DEDUPE = 8192;
  if (url.length > MAX_URL_FOR_DEDUPE) return false;
  return method === 'GET' || method === 'HEAD';
};

const cloneFetchOutcomeForConsumer = (outcome: SafeType<Response>): SafeType<Response> => {
  if (outcome.ok) {
    try {
      return { ok: outcome.ok.clone() };
    } catch {
      return outcome;
    }
  }
  if (outcome.err instanceof SafeError) {
    const sourceData = outcome.err.data;
    // Error instances (e.g. the native TypeError from a failed fetch) keep `message`,
    // `name`, `stack`, and `cause` as non-enumerable properties — `{ ...err }` would drop
    // them all and reduce `data` to `{}`, hiding the underlying cause.code that
    // logHttpError uses to print a friendly "Connection failed: <code>" line.
    const clonedData =
      sourceData !== undefined &&
      typeof sourceData === 'object' &&
      sourceData !== null &&
      !(sourceData instanceof Error)
        ? { ...(sourceData as Record<string, unknown>) }
        : sourceData;
    return { err: new SafeError(outcome.err.message, outcome.err.type, clonedData as typeof outcome.err.data) };
  }
  return outcome;
};

export const coalesceOutboundFetch = (
  input: RequestInfo,
  init: OutboundDedupeRequestInit | undefined,
  fetch: () => Promise<SafeType<Response>>,
): Promise<SafeType<Response>> => {
  if (!shouldDedupeOutboundRequest(input, init)) {
    return fetch();
  }

  const cacheKey = buildOutboundRequestDedupeKey(input, init);
  const inFlight = getInFlightMap();
  const existing = inFlight.get(cacheKey);
  if (existing) {
    return existing.then(cloneFetchOutcomeForConsumer);
  }

  const request = fetch().finally(() => {
    if (inFlight.get(cacheKey) === request) {
      inFlight.delete(cacheKey);
    }
  });
  inFlight.set(cacheKey, request);
  return request.then(cloneFetchOutcomeForConsumer);
};
